import { useCallback, useEffect, useState } from "react";
import {
  Droplets, LogOut, Search, Download, Users, Sparkles, FileCheck2, Trophy,
  TrendingUp, FileText, Loader2, X, StickyNote, ExternalLink, Filter
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { US_STATES, SERVICES } from "@/data/content";

const STATUSES = ["New", "Contacted", "Qualified", "Proposal Sent", "Won", "Lost"];
const STATUS_COLORS = {
  New: "bg-blue-100 text-blue-800",
  Contacted: "bg-amber-100 text-amber-800",
  Qualified: "bg-purple-100 text-purple-800",
  "Proposal Sent": "bg-cyan-100 text-cyan-800",
  Won: "bg-emerald-100 text-emerald-800",
  Lost: "bg-slate-200 text-slate-600",
};

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent}`}>
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
      </div>
      <p className="mt-3 text-2xl font-extrabold text-navy-900 tabular-nums">{value}</p>
      <p className="text-xs font-semibold text-slate-500">{label}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fType, setFType] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fState, setFState] = useState("all");
  const [fService, setFService] = useState("all");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (fType !== "all") params.type = fType;
      if (fStatus !== "all") params.status = fStatus;
      if (fState !== "all") params.state = fState;
      if (fService !== "all") params.service = fService;
      const [a, l] = await Promise.all([api.get("/admin/analytics"), api.get("/admin/leads", { params })]);
      setAnalytics(a.data);
      setLeads(l.data);
    } catch (e) {
      toast.error("Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
   }, [fType, fStatus, fState, fService, q]);

  useEffect(() => {   
    loadAll(); 
  }, [loadAll]);
  const openLead = async (id) => {
    try {
      const res = await api.get(`/admin/leads/${id}`);
      setSelected(res.data);
    } catch { toast.error("Failed to load lead."); }
  };

  const changeStatus = async (status) => {
    if (!selected) return;
    try {
      await api.patch(`/admin/leads/${selected.id}/status`, { status });
      setSelected((s) => ({ ...s, status }));
      setLeads((prev) => prev.map((l) => (l.id === selected.id ? { ...l, status } : l)));
      loadAll();
      toast.success("Status updated.");
    } catch { toast.error("Failed to update status."); }
  };

  const addNote = async () => {
    if (!note.trim() || !selected) return;
    setSavingNote(true);
    try {
      const res = await api.post(`/admin/leads/${selected.id}/notes`, { text: note });
      setSelected((s) => ({ ...s, notes: [...(s.notes || []), res.data] }));
      setNote("");
      toast.success("Note added.");
    } catch { toast.error("Failed to add note."); } finally { setSavingNote(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/leads-export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "wasserlab_leads.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };

  const viewFile = (fileId) => {
    const token = localStorage.getItem("wl_token");
    window.open(`${API}/admin/files/${fileId}/download?auth=${token}`, "_blank");
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-[1400px] px-4 lg:px-6 flex h-16 items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-navy-900 text-amber-500">
              <Droplets className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p className="font-heading text-base font-extrabold text-navy-900 leading-none">Wasserlab</p>
              <p className="text-[11px] text-slate-500">Compliance Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-slate-600">{user?.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="logout-button">
              <LogOut className="h-4 w-4" aria-hidden="true" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 lg:px-6 py-6">
        {/* Analytics */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="analytics-grid">
          <StatCard icon={Users} label="Total Leads" value={analytics?.total ?? "—"} accent="bg-navy-900 text-amber-500" />
          <StatCard icon={Sparkles} label="New Leads" value={analytics?.new ?? "—"} accent="bg-blue-100 text-blue-700" />
          <StatCard icon={FileCheck2} label="Qualified" value={analytics?.qualified ?? "—"} accent="bg-purple-100 text-purple-700" />
          <StatCard icon={FileText} label="Proposals" value={analytics?.proposals ?? "—"} accent="bg-cyan-100 text-cyan-700" />
          <StatCard icon={Trophy} label="Won Projects" value={analytics?.won ?? "—"} accent="bg-emerald-100 text-emerald-700" />
          <StatCard icon={TrendingUp} label="Conversion Rate" value={analytics ? `${analytics.conversion_rate}%` : "—"} accent="bg-amber-100 text-amber-700" />
        </div>

        {/* Toolbar */}
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <form onSubmit={(e) => { e.preventDefault(); loadAll(); }} className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" aria-hidden="true" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company..." className="pl-9" data-testid="lead-search" />
            </form>
            <div className="flex flex-wrap items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" aria-hidden="true" />
              <Select value={fType} onValueChange={setFType}>
                <SelectTrigger className="w-[140px] h-9" data-testid="filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="assessment">Assessments</SelectItem>
                  <SelectItem value="contact">Contact Requests</SelectItem>
                </SelectContent>
              </Select>
              <Select value={fStatus} onValueChange={setFStatus}>
                <SelectTrigger className="w-[150px] h-9" data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={fService} onValueChange={setFService}>
                <SelectTrigger className="w-[170px] h-9" data-testid="filter-service"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All Services</SelectItem>
                  {SERVICES.map((s) => <SelectItem key={s.slug} value={s.name}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={fState} onValueChange={setFState}>
                <SelectTrigger className="w-[140px] h-9" data-testid="filter-state"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All States</SelectItem>
                  {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <button onClick={exportCsv} className="inline-flex items-center gap-1.5 rounded-md bg-navy-900 px-3 py-2 text-sm font-semibold text-white hover:bg-navy-800" data-testid="export-csv">
                <Download className="h-4 w-4" aria-hidden="true" /> Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Leads table */}
        <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Company</th>
                  <th className="px-4 py-3 font-semibold">Type</th>
                  <th className="px-4 py-3 font-semibold">State</th>
                  <th className="px-4 py-3 font-semibold">Service</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Received</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></td></tr>
                ) : leads.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-16 text-center text-slate-500" data-testid="no-leads">No leads found.</td></tr>
                ) : (
                  leads.map((l) => (
                    <tr key={l.id} onClick={() => openLead(l.id)} className="border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors" data-testid={`lead-row-${l.id}`}>
                      <td className="px-4 py-3 font-semibold text-navy-900">{l.first_name} {l.last_name}</td>
                      <td className="px-4 py-3 text-slate-600">{l.company || "—"}</td>
                      <td className="px-4 py-3"><span className="text-xs font-medium capitalize text-slate-600">{l.type}</span></td>
                      <td className="px-4 py-3 text-slate-600">{l.state || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-[180px] truncate">{l.service_interest || "—"}</td>
                      <td className="px-4 py-3"><Badge className={`${STATUS_COLORS[l.status]} hover:${STATUS_COLORS[l.status]} border-0 font-semibold`}>{l.status}</Badge></td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Lead detail sheet */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="lead-detail">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading text-xl text-navy-900">{selected.first_name} {selected.last_name}</SheetTitle>
                <p className="text-sm text-slate-500 capitalize">{selected.type} · {fmtDate(selected.created_at)}</p>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Status</Label>
                  <Select value={selected.status} onValueChange={changeStatus}>
                    <SelectTrigger className="mt-1.5" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="detail-fields">
                  {[
                    ["Email", selected.email], ["Company", selected.company], ["Job Title", selected.job_title],
                    ["Phone", selected.phone], ["State", selected.state], ["Facility Type", selected.facility_type],
                    ["# Facilities", selected.num_facilities], ["# Chemicals", selected.num_chemicals],
                    ["Current Process", selected.current_process], ["Audience", selected.audience],
                    ["Target Deadline", selected.target_deadline], ["Service Interest", selected.service_interest],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4 px-3 py-2 text-sm">
                      <span className="text-slate-500">{k}</span>
                      <span className="font-medium text-navy-900 text-right break-all">{v}</span>
                    </div>
                  ))}
                </div>

                {selected.message && (
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-500">Message</Label>
                    <p className="mt-1.5 rounded-lg bg-slate-50 p-3 text-sm text-slate-700 whitespace-pre-wrap">{selected.message}</p>
                  </div>
                )}

                {selected.files?.length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-500">Uploaded Documents</Label>
                    <ul className="mt-1.5 space-y-2" data-testid="detail-files">
                      {selected.files.map((f) => (
                        <li key={f.id}>
                          <button onClick={() => viewFile(f.id)} className="flex w-full items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50" data-testid={`detail-file-${f.id}`}>
                            <span className="flex items-center gap-2 truncate text-slate-700"><FileText className="h-4 w-4 text-emerald-600" aria-hidden="true" /><span className="truncate">{f.original_filename}</span></span>
                            <ExternalLink className="h-4 w-4 text-slate-400" aria-hidden="true" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><StickyNote className="h-3.5 w-3.5" /> Internal Notes</Label>
                  <div className="mt-2 space-y-2">
                    {(selected.notes || []).map((n, i) => (
                      <div key={i} className="rounded-lg bg-amber-50 border border-amber-100 p-3 text-sm" data-testid={`note-${i}`}>
                        <p className="text-slate-700 whitespace-pre-wrap">{n.text}</p>
                        <p className="mt-1 text-xs text-slate-400">{n.author} · {fmtDate(n.created_at)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add an internal note..." data-testid="note-input" />
                  </div>
                  <button onClick={addNote} disabled={savingNote || !note.trim()} className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-navy-900 px-4 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-50" data-testid="add-note-button">
                    {savingNote && <Loader2 className="h-4 w-4 animate-spin" />} Add Note
                  </button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
